<script setup>
import { ref, computed, onMounted } from 'vue'
import { fetchWorksFilters, fetchWorks } from '@/services/api'

/* API DATA */
const works = ref([])
const filtersData = ref({
    categories: [],
    clients: [],
    industries: [],
    sectors: [],
    services: []
})
const meta = ref({
    current_page: 1,
    per_page: 6,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
})

/* LOADING STATE */
const loading = ref(false)
const filtersLoading = ref(false)

/* FILTER FORM (UI STATE) */
const filters = ref({
    client: '',
    industry: '',
    sector: '',
    category: '',
    service: '',
    year: '',
    orderBy: 'latest'
})

/* APPLIED FILTER (REAL FILTER STATE) */
const appliedFilters = ref({ ...filters.value })

/* CURRENT PAGE */
const currentPage = ref(1)

/* Build filter params for API */
const buildApiParams = () => {
    const params = { page: currentPage.value }

    if (appliedFilters.value.category) {
        params.category_id = Number(appliedFilters.value.category)
    }
    if (appliedFilters.value.client) {
        params.client_id = Number(appliedFilters.value.client)
    }
    if (appliedFilters.value.industry) {
        params.industry_id = Number(appliedFilters.value.industry)
    }
    if (appliedFilters.value.service) {
        params.service_id = Number(appliedFilters.value.service)
    }
    if (appliedFilters.value.sector) {
        params.sector_id = Number(appliedFilters.value.sector)
    }

    if (appliedFilters.value.orderBy) {
        params.order_by = appliedFilters.value.orderBy
    }

    return params
}

/* Fetch filter data */
const fetchFiltersData = async () => {
    filtersLoading.value = true
    try {
        const data = await fetchWorksFilters()
        filtersData.value = data
    } catch (error) {
        console.error('Failed to fetch filters:', error)
    } finally {
        filtersLoading.value = false
    }
}

/* Fetch works data */
const fetchWorksData = async (append = false) => {
    loading.value = true
    try {
        const params = buildApiParams()
        const result = await fetchWorks(params)
        if (append) {
            works.value = [...works.value, ...result.data]
        } else {
            works.value = result.data
        }
        meta.value = result.meta
    } catch (error) {
        console.error('Failed to fetch works:', error)
        if (!append) {
            works.value = []
        }
    } finally {
        loading.value = false
    }
}

/* Client-side year filter (since API doesn't support year filtering) */
const filteredWorks = computed(() => {
    if (!appliedFilters.value.year) {
        return works.value
    }
    return works.value.filter(item => item.year === Number(appliedFilters.value.year))
})

/* Get unique years from works for year filter */
const availableYears = computed(() => {
    const years = new Set(works.value.map(item => item.year))
    return Array.from(years).sort((a, b) => b - a)
})

/* Get industry name by client ID */
const getIndustryByClientId = (clientId) => {
    const client = filtersData.value.clients.find(c => c.id === clientId)
    // Note: API doesn't return industry with client data
    // This would need backend modification or separate mapping
    return ''
}

/* Get client name by ID */
const getClientName = (clientId) => {
    const client = filtersData.value.clients.find(c => c.id === clientId)
    return client?.name || ''
}

/* Get category name */
const getCategoryName = (workItem) => {
    return workItem.categories?.[0]?.name || ''
}

/* APPLY FILTER */
const applyFilter = () => {
    appliedFilters.value = { ...filters.value }
    currentPage.value = 1
    fetchWorksData()
}

/* RESET FILTER */
const resetFilter = () => {
    filters.value = {
        client: '',
        industry: '',
        sector: '',
        category: '',
        service: '',
        year: '',
        orderBy: 'latest'
    }
    appliedFilters.value = { ...filters.value }
    currentPage.value = 1
    fetchWorksData()
}

/* Load more works */
const loadMore = () => {
    if (meta.value.has_more_pages && !loading.value) {
        currentPage.value++
        fetchWorksData(true)
    }
}

/* Initial fetch */
onMounted(() => {
    fetchFiltersData()
    fetchWorksData()
})
</script>

<template>
    <!-- FILTER SECTION -->
    <section class="section-filter bg-dark py-4 py-md-5">
        <div class="container">
            <div class="row g-2 align-items-center">

                <!-- CLIENT -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.client" class="form-select rounded-pill form-dark"
                        :disabled="filtersLoading">
                        <option value="">All Client</option>
                        <option v-for="client in filtersData.clients" :key="client.id" :value="client.id">
                            {{ client.name }}
                        </option>
                    </select>
                </div>

                <!-- INDUSTRY -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.industry" class="form-select rounded-pill form-dark"
                        :disabled="filtersLoading">
                        <option value="">All Industry</option>
                        <option v-for="industry in filtersData.industries" :key="industry.id" :value="industry.id">
                            {{ industry.name }}
                        </option>
                    </select>
                </div>

                <!-- SECTOR -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.sector" class="form-select rounded-pill form-dark"
                        :disabled="filtersLoading">
                        <option value="">All Sector</option>
                        <option v-for="sector in filtersData.sectors" :key="sector.id" :value="sector.id">
                            {{ sector.name }}
                        </option>
                    </select>
                </div>
                <!-- SERVICE  -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.service" class="form-select rounded-pill form-dark"
                        :disabled="filtersLoading">
                        <option value="">All Services</option>
                        <option v-for="service in filtersData.services" :key="service.id" :value="service.id">
                            {{ service.title }}
                        </option>
                    </select>
                </div>

                <!-- YEAR -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.year" class="form-select rounded-pill form-dark">
                        <option value="">All Year</option>
                        <option v-for="year in availableYears" :key="year" :value="year">
                            {{ year }}
                        </option>
                    </select>
                </div>

                <!-- ORDER BY -->
                <div class="col-6 col-md-6 col-lg">
                    <select v-model="filters.orderBy" class="form-select rounded-pill form-dark">
                        <option value="latest">Latest</option>
                        <option value="popular">Popular</option>
                    </select>
                </div>

                <!-- BUTTON -->
                <div class="col-12 col-md-12 col-lg-auto">
                    <div class="row g-2">
                        <div class="col-6 col-lg-auto">
                            <button type="button" class="btn btn-secondary rounded-pill w-100 px-4"
                                style="color:#820358" @click="applyFilter" :disabled="loading">
                                {{ loading ? 'Loading...' : 'Filter' }}
                            </button>
                        </div>

                        <div class="col-6 col-lg-auto">
                            <button type="button" class="btn btn-red rounded-pill w-100 px-4" @click="resetFilter"
                                :disabled="loading">
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- WORKS SECTION -->
    <section class="wrapper bg-black">
        <div class="container pt-10 pb-12 pt-md-14 pb-md-16 text-center">

            <div class="row g-3 mt-8 justify-content-center">
                <div class="col-12 col-md-6 col-lg-4" v-for="item in filteredWorks" :key="item.id">
                    <div class="services-card">

                        <div class="services-image">
                            <img :src="item.image || '/placeholder.jpg'" :alt="item.title" />
                        </div>

                        <div class="team-info">
                            <div class="team-content">
                                <span class="team-name">{{ item.title }}</span>

                                <span class="team-role">
                                    <template v-if="item.month">
                                        {{ item.month }} {{ item.year }}<br>
                                    </template>
                                    <template v-else>
                                        {{ item.year }}<br>
                                    </template>
                                    {{ getCategoryName(item) }} — {{item.clients.map(c => c.name).join(', ')}}
                                </span>

                                <router-link :to="`/works/${item.slug}`" class="view-video-btn">
                                    View Detail
                                </router-link>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- EMPTY STATE -->
                <div v-if="filteredWorks.length === 0 && !loading" class="text-white mt-5">
                    No projects found.
                </div>

                <!-- LOADING STATE -->
                <div v-if="loading" class="text-white mt-5">
                    Loading...
                </div>
            </div>

            <!-- LOAD MORE -->
            <div v-if="meta.has_more_pages && !appliedFilters.year" class="d-flex justify-content-center mt-5">
                <button type="button" class="btn btn-lg btn-secondary rounded-pill" style="color:#820358"
                    @click="loadMore" :disabled="loading">
                    {{ loading ? 'Loading...' : 'Load More' }}
                </button>
            </div>

            <div class="d-flex justify-content-center mt-5">
                <router-link :to="{ name: 'Services' }" class="btn btn-lg btn-secondary rounded-pill"
                    style="color:#820358">
                    Learn More About Our Services
                </router-link>
            </div>

        </div>
    </section>
</template>

<style>
.section-filter {
    position: sticky;
    top: 70px;
    z-index: 1020;
}

/* CARD */
.services-card {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    background: #000;
}

.services-image {
    aspect-ratio: 4 / 3;
    overflow: hidden;
}

.services-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
}

.team-info {
    position: absolute;
    inset: 0;
    padding: 20px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: linear-gradient(180deg,
            rgba(130, 3, 88, 0) 51%,
            rgba(63, 1, 48, 1) 100%);
}

.team-content {
    text-align: center;
    transform: translateY(84px);
    transition: transform 0.35s ease;
}

.team-name {
    display: block;
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
}

.team-role {
    display: block;
    margin-top: 6px;
    color: #fff;
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease;
}

.services-card:hover .team-content {
    transform: translateY(0);
}

.services-card:hover .team-role,
.services-card:hover .view-video-btn {
    opacity: 1;
    transform: translateY(0);
}

.services-card:hover img {
    transform: scale(1.05);
}

.view-video-btn {
    display: inline-block;
    margin-top: 10px;
    padding: 6px 16px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #ffae0b;
    text-decoration: none;
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease;
}

.view-video-btn:hover {
    color: #fff;
}

/* FORM */
.form-dark {
    background-color: #1f1f1f;
    border: 1px solid #333;
    color: #fff;
}

.form-dark option {
    background-color: #1f1f1f;
    color: #fff;
}

.form-dark:focus {
    background-color: #1f1f1f;
    border-color: #ffae0b;
    color: #fff;
    box-shadow: none;
}

.form-dark.rounded-pill {
    border-radius: 50px;
}

.form-dark:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
